import React, { ReactNode } from "react";
import { Tab, Tabs, TabScrollButton, TabScrollButtonProps, TabsProps, TabProps, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import i18nPackage from "../../i18n/i18n";
import styles from "./Tabs.module.css";

export type TabsConfig = { key: string; label: ReactNode; icon?: string | React.ReactElement; id?: string };

type TabList = string[] | readonly string[] | TabsConfig[];

type CoreTabProps<T> = {
	value: T;
	tabs: TabList;
	className?: string;
	onChange?: (event: React.SyntheticEvent<Element, Event>, value: T) => void;
	width?: number | string;
	height?: number | string;
	tabsProps?: TabsProps;
	tabProps?: TabProps;
	tabPanelId?: string;
	secondary?: boolean;
	scrollable?: boolean;
	scrollButtons?: TabsProps["scrollButtons"];
};

export function CoreTabNumberIcon({ count = 0, isLoading }: { count?: number; isLoading?: boolean }) {
	if (isLoading) {
		return <Skeleton component="div" variant="circular" className={styles.iconCircle} />;
	}
	return (
		<div className={`${styles.iconCircle} ${styles.number}`}>
			{String(Math.min(count, 99)) + (count >= 100 ? "+" : "")}
		</div>
	);
}

const AccessibleScrollButton = (props: TabScrollButtonProps) => {
	const { direction, ...other } = props;

	return (
		<TabScrollButton
			{...other}
			direction={direction}
			role="button"
			tabIndex={0}
			aria-label={
				direction === "left"
					? i18nPackage.t("coreComponents.tabs.previousButtonAriaLabel")
					: i18nPackage.t("coreComponents.tabs.nextButtonAriaLabel")
			}
		/>
	);
};

const StyledTabs = styled(Tabs)<{ secondary?: boolean }>(({ secondary }) => ({
	...(!secondary
		? {
				borderBottom: "1px solid #bbb7",
				"& .MuiTabs-indicator": {
					backgroundColor: "#614395",
				},
			}
		: {
				"&": {
					minHeight: 46,
				},
				"& .MuiTabs-scroller": {
					padding: "4px",
				},
				"& .MuiTabs-indicator": {
					display: "none",
				},
			}),
	"& .MuiTabScrollButton-root": {
		"&:focus-visible": {
			outline: "2px solid var(--focus-outline-color-dark)",
			outlineOffset: "-10px",
		},
	},
}));

const StyledTab = styled(Tab)<{ secondary?: boolean }>(({ secondary }) => ({
	...(!secondary && {
		"&.Mui-selected": {
			color: "#614395",
		},
	}),
	...(secondary && {
		fontSize: "15px",
		fontWeight: "500 !important",
		color: "#737373",
		padding: "0 8px",
		minHeight: 40,
		transition: "none",
		marginLeft: "3px",
		borderBottom: "2px solid transparent",
		"&.Mui-selected": {
			color: "#614395",
			borderBottom: "2px solid #614395",
			"& .MuiSkeleton-root": {
				backgroundColor: "rgba(255, 255, 255, 0.11)",
			},
			[`& .${styles.iconCircle}.${styles.number}`]: {
				backgroundColor: "white",
				border: "none",
				fontWeight: "500 !important",
			},
		},
		"& .MuiTab-iconWrapper": {
			stroke: "#49454f",
		},
		// Icons come from separate SVG files with their own (inconsistent) native width/height — force a
		// single consistent render size here rather than relying on each file to match.
		"& .MuiTab-iconWrapper svg": {
			width: "16px",
			height: "16px",
		},
		"&.Mui-selected .MuiTab-iconWrapper": {
			stroke: "#614395",
		},
		"&:focus-visible": {
			outline: "2px solid var(--focus-outline-color-dark)",
			outlineOffset: "1px",
			borderRadius: "0",
		},
	}),
}));

export function CoreTabs<T>({
	tabs,
	width,
	height,
	scrollButtons = false,
	tabsProps,
	tabProps,
	tabPanelId,
	...props
}: CoreTabProps<T>) {
	return (
		<StyledTabs
			{...props}
			{...tabsProps}
			variant={tabsProps?.variant ?? (props.scrollable ? "scrollable" : "fullWidth")} // Use scrollable on small screens
			scrollButtons={scrollButtons}
			allowScrollButtonsMobile // Allow scroll buttons on mobile
			ScrollButtonComponent={AccessibleScrollButton}
			sx={{
				width: width,
				maxWidth: "100%",
				minHeight: height,
				...(props.secondary && {
					backgroundColor: "#FFFFFF",
					overflowX: "hidden",
				}),

				...tabsProps?.sx,
			}}
			secondary={props.secondary}
		>
			{tabs.map((tab, index) => (
				<StyledTab
					{...tabProps}
					id={typeof tab === "string" ? undefined : tab.id}
					value={typeof tab === "string" ? index : (tab.key ?? index)}
					label={typeof tab === "string" ? tab : tab.label}
					key={typeof tab === "string" ? index : (tab.key ?? index)}
					iconPosition="start"
					icon={typeof tab === "string" ? undefined : (tab.icon ?? undefined)}
					disableRipple
					style={{
						minHeight: height,
						...tabProps?.style,
					}}
					secondary={props.secondary}
					aria-controls={
						props.value === (typeof tab === "string" ? index : (tab.key ?? index)) ? tabPanelId : undefined
					}
				/>
			))}
		</StyledTabs>
	);
}
